import React from 'react';

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Flex,
  Text,
  Box,
  useMediaQuery,
} from '@chakra-ui/react';

import { ModalRootProps } from 'components/Modal/Root';

import DNITCompositionData from './DNITCompositionData';

type Props = ModalRootProps & {
  data: {
    id: number;
    locale_id: number;
    locale_key: string;
    price_type_id: number;
    price_type_key: string;
    price_type_description: string;
    code: string;
    base: string;
    version: string;
    description: string;
    prices: string;
    price: number;
    classification: string;
    type: string;
    unit_measure: string;
  };
};

const modalMargin = 30;
const modalMobileFix = 10;

const ModalDNITCompositionLookup: React.FC<Props> = ({
  data,
  ...restProps
}) => {
  const price: number = (() => {
    const prices = JSON.parse(data.prices);

    if (prices.length === 0) {
      return data.price || 0;
    }

    const states = Array.from(Object.keys(prices));
    const state = states[0];

    const price_ = prices[state];

    if (price_.default) {
      return price_.default.total;
    }

    if (price_.taxed) {
      return price_.taxed.total;
    }

    if (price_.untaxed) {
      return price_.untaxed.total;
    }

    return 0;
  })();

  const [isMobile] = useMediaQuery('(max-width: 728px)');

  return (
    <Modal {...restProps} scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent
        sx={{
          width: `calc(100% - ${
            isMobile ? modalMargin - modalMobileFix : modalMargin
          }px)`,
          maxWidth: `calc(100% - ${
            isMobile ? modalMargin - modalMobileFix : modalMargin
          }px)`,
          height: `calc(100% - ${
            isMobile ? modalMargin - modalMobileFix : modalMargin
          }px)`,
          maxHeight: `calc(100% - ${
            isMobile ? modalMargin - modalMobileFix : modalMargin
          }px)`,
        }}
      >
        <ModalHeader>Detalhes da composição (DNIT)</ModalHeader>

        <ModalCloseButton />

        <ModalBody>
          <Flex
            width="100%"
            my={4}
            flexDirection={{ base: 'column', md: 'row' }}
            gap={{ base: 2, md: 6 }}
          >
            <Box
              my={{ base: 2, md: 0 }}
              display="flex"
              flexDirection="column"
              width={{ base: '100%', md: '12%' }}
              flexShrink={0}
            >
              <Text fontWeight="600" fontSize="sm">
                Código
              </Text>
              <Text fontSize="sm" fontStyle="italic">
                {data.code}
              </Text>
            </Box>

            <Box
              my={{ base: 2, md: 0 }}
              display="flex"
              flexDirection="column"
              width={{ base: '100%', md: 'auto' }}
              flex="1"
              minWidth={0}
            >
              <Text fontWeight="600" fontSize="sm">
                Descrição
              </Text>
              <Text fontSize="sm" fontStyle="italic">
                {data.description}
              </Text>
            </Box>

            <Box
              my={{ base: 2, md: 0 }}
              display="flex"
              flexDirection="column"
              width={{ base: '100%', md: '18%' }}
              flexShrink={0}
            >
              <Text fontWeight="600" fontSize="sm">
                Valor unitário
              </Text>

              <Text as="span" fontSize="sm" fontStyle="italic">
                {Intl.NumberFormat('pt-BR', {
                  style: 'currency',
                  currency: 'BRL',
                }).format(price)}
              </Text>
            </Box>

            <Box
              my={{ base: 2, md: 0 }}
              display="flex"
              flexDirection="column"
              width={{ base: '100%', md: '18%' }}
              flexShrink={0}
            >
              <Text fontWeight="600" fontSize="sm">
                Produção de equipe
              </Text>

              <Text as="span" fontSize="sm" fontStyle="italic">
                0
              </Text>
            </Box>
          </Flex>

          <Flex
            width="100%"
            my={4}
            flexDirection={{ base: 'column', md: 'row' }}
          >
            <Box
              my={{ base: 2, md: 0 }}
              display="flex"
              flexDirection="column"
              width="100%"
            >
              <Text fontWeight="600" fontSize="sm">
                Base
              </Text>
              <Text fontSize="sm" fontStyle="italic">
                {data.base}
              </Text>
            </Box>

            <Box
              my={{ base: 2, md: 0 }}
              display="flex"
              flexDirection="column"
              width="100%"
            >
              <Text fontWeight="600" fontSize="sm">
                Referência
              </Text>
              <Text fontSize="sm" fontStyle="italic">
                {data.version}
              </Text>
            </Box>

            <Box
              my={{ base: 2, md: 0 }}
              display="flex"
              flexDirection="column"
              width="100%"
            >
              <Text fontWeight="600" fontSize="sm">
                Estado
              </Text>
              <Text fontSize="sm" fontStyle="italic">
                {data.locale_key.toLocaleUpperCase()}
              </Text>
            </Box>

            <Box
              my={{ base: 2, md: 0 }}
              display="flex"
              flexDirection="column"
              width="100%"
            >
              <Text fontWeight="600" fontSize="sm">
                Tipo
              </Text>
              <Text fontSize="sm" fontStyle="italic">
                {data.price_type_description}
              </Text>
            </Box>

            <Box
              my={{ base: 2, md: 0 }}
              display="flex"
              flexDirection="column"
              width="100%"
            >
              <Text fontWeight="600" fontSize="sm">
                Unidade
              </Text>
              <Text fontSize="sm" fontStyle="italic">
                {data.unit_measure}
              </Text>
            </Box>

            <Box
              my={{ base: 2, md: 0 }}
              display="flex"
              flexDirection="column"
              width="100%"
            >
              <Text fontWeight="600" fontSize="sm">
                Composições
              </Text>
              <Text fontSize="sm" fontStyle="italic">
                0
              </Text>
            </Box>

            <Box
              my={{ base: 2, md: 0 }}
              display="flex"
              flexDirection="column"
              width="100%"
            >
              <Text fontWeight="600" fontSize="sm">
                Insumos
              </Text>
              <Text fontSize="sm" fontStyle="italic">
                0
              </Text>
            </Box>

            <Box
              my={{ base: 2, md: 0 }}
              display="flex"
              flexDirection="column"
              width="100%"
            >
              <Text fontWeight="600" fontSize="sm">
                FIC
              </Text>
              <Text fontSize="sm" fontStyle="italic">
                0
              </Text>
            </Box>
          </Flex>

          <DNITCompositionData />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ModalDNITCompositionLookup;
